import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  breakpointAnalysisUrl,
  captableUrl,
  financialUrl,
  firmSummaryUrl,
  fundOwnershipUrl,
  fundScheduleOfInvestmentsUrl,
  fundSummaryUrl,
  valuationUrl,
} from 'common/config/urls';
import {
  PDF_EXPORT_QUERY_PARAM_EXPORT_MODE,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TAB,
  PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET,
} from 'common/constants/pdfExport';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { FIRM_ROUTES } from '../../../pages/Firms/firm.routes';
import { PdfExportConfiguration, PdfExportOption } from '../PdfExportConfigDialog.types';
import { PdfExportPageTitles, PdfExportSupportedQueryParams, PdfExportTarget, PdfPage } from './types';

const getRouteSlugById = (routes: { id: string; slug?: string }[], id: string): string | undefined =>
  routes.find(r => r.id === id)?.slug;

const withQuery = (path: string, params: PdfExportSupportedQueryParams): string => {
  const url = new URL(path, window.location.origin);
  if (params.date) {
    url.searchParams.set('date', params.date);
  }
  if (params.version) {
    url.searchParams.set('version', params.version);
  }
  if (params.exportMode) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_MODE, params.exportMode);
  }
  if (params.exportTarget) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TARGET, params.exportTarget);
  }
  if (params.exportTab) {
    url.searchParams.set(PDF_EXPORT_QUERY_PARAM_EXPORT_TAB, params.exportTab);
  }
  return url.toString();
};

/**
 * Hook to build dynamic URLs for PDF export based on current context and selected configuration
 */
export const usePdfPages = (configuration: PdfExportConfiguration): PdfPage[] => {
  const location = useLocation();
  const params = useParams();
  const [storeValues] = useStore() as unknown as UseStoreValues;
  const { companyInfo, firmInfo } = storeValues;

  return useMemo(() => {
    // Build URLs dynamically based on current context and configuration
    const firmSlug = firmInfo?.slug || params.firmSlugParam;
    const companySlug = companyInfo?.slug || params.companySlugParam;
    const fundSlug = params.fundSlugParam;

    // Extract current query parameters
    const currentSearchParams = new URLSearchParams(location.search);
    const measurementDate = currentSearchParams.get('date');
    const version = currentSearchParams.get('version');

    // Common query params
    const commonQuery = {
      date: measurementDate,
      version,
      exportMode: 'true',
    } as const;

    // Build URLs based on selected configuration
    const pages: PdfPage[] = [];

    const pushIf = (
      enabled: boolean,
      builder: (fs: string, cs: string) => string,
      title: PdfExportPageTitles,
      exportTarget: PdfExportTarget,
      exportTab?: string
    ) => {
      if (!enabled || !firmSlug || !companySlug) {
        return;
      }
      pages.push({
        url: withQuery(builder(firmSlug, companySlug), { ...commonQuery, exportTarget, exportTab }),
        title,
        exportTarget,
        exportTab,
      });
    };

    switch (configuration.selectedOption) {
      case PdfExportOption.VALUATIONS:
      case PdfExportOption.OTHER:
        if (companySlug) {
          const { companyOptions } = configuration;
          pushIf(
            companyOptions.valuationSummary,
            valuationUrl,
            PdfExportPageTitles.VALUATION_SUMMARY,
            PdfExportTarget.VALUATION_SUMMARY_TABLE
          );
          pushIf(
            companyOptions.equityAllocation,
            valuationUrl,
            PdfExportPageTitles.EQUITY_ALLOCATION,
            PdfExportTarget.VALUATION_EQUITY_ALLOCATION_TABLE
          );
          pushIf(
            companyOptions.concludedShareValues,
            valuationUrl,
            PdfExportPageTitles.CONCLUDED_SHARE_VALUES,
            PdfExportTarget.VALUATION_CONCLUDED_SHARE_VALUES_TABLE
          );
          pushIf(
            companyOptions.financials,
            financialUrl,
            PdfExportPageTitles.FINANCIALS,
            PdfExportTarget.FINANCIALS_TABLES
          );
          pushIf(companyOptions.capTable, captableUrl, PdfExportPageTitles.CAP_TABLE, PdfExportTarget.CAP_TABLE);
          pushIf(
            companyOptions.fundOwnership,
            fundOwnershipUrl,
            PdfExportPageTitles.FUND_OWNERSHIP,
            PdfExportTarget.FUND_OWNERSHIP_TABLE
          );
          pushIf(
            companyOptions.breakpointAnalysis,
            breakpointAnalysisUrl,
            PdfExportPageTitles.BREAKPOINT_ANALYSIS,
            PdfExportTarget.BREAKPOINTS_TABLE
          );
          pushIf(
            companyOptions.backsolve,
            valuationUrl,
            PdfExportPageTitles.BACKSOLVE,
            PdfExportTarget.BACKSOLVE_TABLES,
            PdfExportPageTitles.BACKSOLVE,
          );
        }
        break;

      case PdfExportOption.FUND:
        if (fundSlug) {
          const { fundOptions } = configuration;

          if (fundOptions.summary) {
            pages.push({
              url: withQuery(fundSummaryUrl(firmSlug!, fundSlug), {
                ...commonQuery,
                exportTarget: PdfExportTarget.FUND_SUMMARY_TABLE,
              }),
              title: PdfExportPageTitles.FUND_SUMMARY,
              exportTarget: PdfExportTarget.FUND_SUMMARY_TABLE,
            });
          }
          if (fundOptions.scheduleOfInvestments) {
            pages.push({
              url: withQuery(fundScheduleOfInvestmentsUrl(firmSlug!, fundSlug), {
                ...commonQuery,
                exportTarget: PdfExportTarget.FUND_SCHEDULE_OF_INVESTMENTS_TABLES,
              }),
              title: PdfExportPageTitles.SCHEDULE_OF_INVESTMENTS,
              exportTarget: PdfExportTarget.FUND_SCHEDULE_OF_INVESTMENTS_TABLES,
            });
          }
        }
        break;

      case PdfExportOption.FIRM:
        if (firmSlug) {
          const { firmOptions } = configuration;

          if (firmOptions.summary) {
            const summarySlug = getRouteSlugById(FIRM_ROUTES, 'summary');
            if (summarySlug) {
              pages.push({
                url: withQuery(firmSummaryUrl(firmSlug!), {
                  ...commonQuery,
                  exportTarget: PdfExportTarget.FIRM_SUMMARY_TABLE,
                }),
                title: PdfExportPageTitles.FIRM_SUMMARY,
                exportTarget: PdfExportTarget.FIRM_SUMMARY_TABLE,
              });
            }
          }
        }
        break;

      default:
        // No additional URLs for unknown options
        break;
    }

    return pages;
  }, [configuration, location.search, params, companyInfo, firmInfo]);
};
